import axios from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import React, { useEffect, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { addCampaign, editCampaign } from 'services/emailCampaignServices';
import { EmailCampaignDto, UpsertEmailCampaignDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: (successfullySaved: boolean) => void,
  campaignId?: number,
  campaign?: EmailCampaignDto
}

let initialValues: Partial<UpsertEmailCampaignDto> = {};

const UpsertEmailCampaign = (props: Props) => {

  const {
    campaignId,
    campaign,
    onSave,
    onCancel,
    getMessage
  } = props;

  const [campaignLoaded, setCampaignLoaded] =
      useState(false);

  const [errorMessage, setErrorMessage] =
      useState(undefined as string | undefined);

  const cancelTokenSource = axios.CancelToken.source();

  useEffect(() => {

    initialValues = {
      name: "",
      template: "",
      active: false,
      requiresConsent: false
    }

    if (campaignId) {
      initialValues = {...campaign}
    }

    setCampaignLoaded(true);

  }, []);

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.unknownEmailCampaign
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }

  const handleSubmit = async (values: Partial<UpsertEmailCampaignDto>) => {

    const request = {...values};

    if (campaignId) {
      await editCampaign(campaignId, request, cancelTokenSource)
          .then(() => onSave(true))
          .catch((e: any) => handleError(e.response.data));
    } else {
      await addCampaign(request, cancelTokenSource)
          .then(() => onSave(true))
          .catch((e: any) => handleError(e.response.data));
    }

  }

  const renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    return (
        <form onSubmit={handleSubmit}>
          {
            !campaignLoaded
                ? <LoaderComponent message={getMessage("loading")}/>
                : <FormContainer>
                  {errorMessage &&
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                            {errorMessage}
                          </StyledErrorMessage>
                        </Grid.Column>
                      </Grid.Row>
                  }
                  <PxGrid>
                    <Grid.Row>
                      <Grid.Column width={5}>
                        <Field
                            name="active"
                            component={CheckBox}
                            label={`${getMessage("emailCampaign.active.label")}`}
                            toggle
                        />
                      </Grid.Column>
                      <Grid.Column width={5}>
                        <Field
                            name="requiresConsent"
                            component={CheckBox}
                            label={`${getMessage("emailCampaign.requiresConsent.label")}`}
                            toggle
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={2}>{getMessage("emailCampaign.name.label")}</Grid.Column>
                      <Grid.Column width={6}>
                        <Field
                            name="name"
                            component={Input}
                            validate={required}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={2}>{getMessage("emailCampaign.subject.label")}</Grid.Column>
                      <Grid.Column width={6}>
                        <Field
                            name="subject"
                            component={Input}
                            validate={required}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={2}>{getMessage("emailCampaign.template.label")}</Grid.Column>
                      <Grid.Column width={6}>
                        <Field
                            name="template"
                            component={TextArea}
                            validate={required}
                            style={{
                              minHeight: "20vh",
                              maxHeight: "70vh",
                              height: "20vh",
                              minWidth: "20vh",
                              maxWidth: "70vh",
                              width: "50vh"
                            }}
                            disabled={campaignId}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </PxGrid>
                  <FormButtonsRow>
                    <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
                    <Button compact type="button" disabled={submitting}
                            onClick={onCancel}>{getMessage("button.cancel")}</Button>
                  </FormButtonsRow>
                </FormContainer>
          }
        </form>
    )
  }

  return (
      <FinalForm
          onSubmit={(values) => handleSubmit(values)}
          initialValues={initialValues}
          subscription={{values: true, pristine: true, submitting: true}}
          render={renderForm}
      />
  );
}

export default withMessages(UpsertEmailCampaign);
